import { useCallback, useReducer } from "react";
import type { AuthInfo } from "../types";

type AuthInfoState = {
  authInfoMap: Map<string, AuthInfo>;
};

type AuthInfoAction =
  | { type: "AUTH_REQUIRED"; info: AuthInfo }
  | { type: "AUTH_COMPLETED"; providerKey: string }
  | { type: "AUTH_FAILED"; providerKey: string }
  | { type: "AUTH_POLLING_STOPPED" }
  | { type: "CLEAR_AUTH_INFO" };

function authInfoReducer(
  state: AuthInfoState,
  action: AuthInfoAction
): AuthInfoState {
  switch (action.type) {
    case "AUTH_REQUIRED": {
      const next = new Map(state.authInfoMap);
      next.set(action.info.providerKey, action.info);
      return { authInfoMap: next };
    }

    case "AUTH_COMPLETED": {
      const existing = state.authInfoMap.get(action.providerKey);
      if (!existing) {
        return state;
      }
      const next = new Map(state.authInfoMap);
      next.set(action.providerKey, { ...existing, status: "completed" });
      return { authInfoMap: next };
    }

    case "AUTH_FAILED": {
      const existing = state.authInfoMap.get(action.providerKey);
      if (!existing) {
        return state;
      }
      const next = new Map(state.authInfoMap);
      next.set(action.providerKey, { ...existing, status: "failed" });
      return { authInfoMap: next };
    }

    case "AUTH_POLLING_STOPPED": {
      let changed = false;
      const next = new Map(state.authInfoMap);
      for (const [providerKey, info] of next.entries()) {
        if (info.status === "pending" || info.status === undefined) {
          next.set(providerKey, { ...info, status: "stopped" });
          changed = true;
        }
      }
      return changed ? { authInfoMap: next } : state;
    }

    case "CLEAR_AUTH_INFO":
      return { authInfoMap: new Map() };

    default:
      return state;
  }
}

const INITIAL_STATE: AuthInfoState = { authInfoMap: new Map() };

type StreamDataPart = { type: string; data?: unknown };

const AUTH_ACTION_MAP: Record<string, "AUTH_COMPLETED" | "AUTH_FAILED"> = {
  "data-auth-completed": "AUTH_COMPLETED",
  "data-auth-failed": "AUTH_FAILED",
};

function parseAuthAction(dataPart: StreamDataPart): AuthInfoAction | null {
  if (dataPart.type === "data-auth-required") {
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const info = dataPart.data as AuthInfo | undefined;
    return info?.providerKey ? { type: "AUTH_REQUIRED", info } : null;
  }
  const actionType = AUTH_ACTION_MAP[dataPart.type];
  if (!actionType) {
    return null;
  }
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  const key = (dataPart.data as { providerKey?: string })?.providerKey;
  return typeof key === "string"
    ? { type: actionType, providerKey: key }
    : null;
}

export function useAuthInfo() {
  const [state, dispatch] = useReducer(authInfoReducer, INITIAL_STATE);

  const handleDataPart = useCallback((dataPart: StreamDataPart) => {
    const action = parseAuthAction(dataPart);
    if (action) {
      dispatch(action);
    }
  }, []);

  const stopPolling = useCallback(
    () => dispatch({ type: "AUTH_POLLING_STOPPED" }),
    []
  );

  const clear = useCallback(() => dispatch({ type: "CLEAR_AUTH_INFO" }), []);

  return {
    authInfoMap: state.authInfoMap,
    handleDataPart,
    stopPolling,
    clear,
  };
}
